/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { DataPoints } from '../DataPoints';
import { PercentageCell, NotApplicableCell } from '../../../../views/components/cells/index';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { REGIONS, ASSET_ALLOCATION_GROUPS } from '../../../../utils/constants';
import { getCountryName } from '../../../../utils/i18n';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import Dictionary from '@/types/dictionary';
import DonutData from '@/types/donut-data';

/**
 * @class AssetAllocation Data Access Object
 */
export default class FixedIncomeRegionAllocationDAO extends ComparisonItemBasedProposalDAO {
  countryExclusionMap: Dictionary<string[]> | null = null;

  regionExclusions: string[] | null = null;

  dataKeys: string[] = Object.values(REGIONS);

  countryKeys: string[] = [];

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new FixedIncomeRegionAllocationDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['REGION_ALLOCATION_COLORS', 'DEFAULT'];
  }

  // eslint-disable-next-line class-methods-use-this
  private hasEmptyRegions(itemDAO: ComparisonItemDAO) {
    const fixedIncomeAllocation = DataPoints.getAssetAllocationTotalsByCategory(itemDAO.item, ASSET_ALLOCATION_GROUPS.FIXED_INCOME).response;
    return (
      fixedIncomeAllocation > 0 &&
      itemDAO.item.allocations &&
      itemDAO.item.allocations.fixedIncomeRegion &&
      itemDAO.item.allocations.fixedIncomeRegion.length === 0
    );
  }

  protected build(): void {
    // test for options
    const daoConfig: ProposalNS.FixedIncomeRegionAllocationConfig = this.proposalElement.DAO as ProposalNS.FixedIncomeRegionAllocationConfig;

    if (daoConfig.regionExclusionConfigDAOOptions !== undefined) {
      this.countryExclusionMap = daoConfig.regionExclusionConfigDAOOptions.countryExclusions;
      for (const values of Object.values(this.countryExclusionMap)) {
        this.countryKeys.push(...values);
      }
      this.regionExclusions = daoConfig.regionExclusionConfigDAOOptions.regionExclusions;
    }

    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      let otherAllocations: number = 0;

      for (const key of this.countryKeys.concat(this.dataKeys)) {
        let regionAllocation = DataPoints.getFixedIncomeRegionAllocationByKey(itemDAO.item, key);

        const fieldName = this.dataKeys.includes(key) ? this.formatMessage(`Comparison.Table.FixedIncomeRegions.${key}`) : getCountryName(key, this.locale);
        if (this.countryExclusionMap) {
          regionAllocation = DataPoints.getFixedIncomeRegionWithCountry(itemDAO.item, key, this.countryExclusionMap);
        }
        if (daoConfig.rollIntoOther && this.hasEmptyRegions(itemDAO) && key.toUpperCase() === REGIONS.OTHER) {
          regionAllocation.response = 100;
          this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
        }
        this.addDisclaimerKeys(regionAllocation.disclaimerKeys, itemDAO.item);

        if (this.regionExclusions && this.regionExclusions.indexOf(key) !== -1) {
          otherAllocations += regionAllocation.response;
        } else {
          let color: string | undefined;
          if (this.colorsDict) {
            color = this.colorsDict[key];
          }
          this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableContent, color);

          let value: number = regionAllocation.response ? regionAllocation.response : 0;
          if (daoConfig.rollIntoOther && key === REGIONS.OTHER) {
            this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
            value += otherAllocations;
          }
          const field = new ProposalFieldDAO(fieldName, value, ProposalStyleClassNames.ProposalTableContent);

          field.cellType = value === 0 ? NotApplicableCell : PercentageCell;
          field.key = key;
          this.addFieldDAO(itemDAO, field);
        }
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }

    return out;
  }
}
